import { useEffect, useMemo } from 'react'
import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'
import { usePromotionItemsAvailabilityQuery } from '../../graphQL/queries/promotionItemsAvailability'
import type { IAvailabilityProduct } from '../../graphQL/queries/promotionItemsAvailability/types'
import type { IProduct } from '../../interfaces'
import { setPromotionsAvailability } from '../../redux/slices/catalog'
import { isDisableUnfulfillableAddAllOn } from '../../utils/promotions/addAllAvailability'
import { transformSelectedProduct } from '../../utils/catalog/transformSelectedProduct'
import { getFlattedCartItems } from '../../utils/utilsForCatalogReducer'
import { generateDataDogContext } from '../../utils/dataDog'
import { isNumber } from '../../utils/validTypes'
import {
  IAddAllAnomaly,
  collectAddAllSkus,
  computeAddAllAvailability,
  getAddAllPromotions,
} from './promotionAddAllAvailability'

const FUNCTION_NAME = 'usePromotionAddAllAvailability'
export const LOOKUP_FAILURE_TITLE =
  'could not check promotion add-all availability'
export const COMPUTE_FAILURE_TITLE =
  'promotion add-all availability rule failed'
export const ANOMALY_TITLE = 'promotion add-all availability anomaly'

/**
 * How many anomalies and SKUs travel with a report. The browser SDK drops
 * events over its size limit, so an unbounded list would lose the signal on
 * exactly the promotions that name the most products.
 */
export const ANOMALY_REPORT_LIMIT = 20

/**
 * SKUs per request. The storefront's search caps its result with
 * `$limit: getMaxProductSearchResults()` and no sort, so a request asking for
 * more SKUs than the cap loses an arbitrary subset — which would read here as
 * those products being missing. 20 is that setting's code default; an
 * environment that sets it lower would need this lowered too.
 */
export const PROMOTION_AVAILABILITY_CHUNK_SIZE = 20

const chunk = (skus: string[]): string[][] =>
  Array.from(
    { length: Math.ceil(skus.length / PROMOTION_AVAILABILITY_CHUNK_SIZE) },
    (_, index) =>
      skus.slice(
        index * PROMOTION_AVAILABILITY_CHUNK_SIZE,
        (index + 1) * PROMOTION_AVAILABILITY_CHUNK_SIZE,
      ),
  )

class LookupAnswerError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LookupAnswerError'
  }
}

type FailureStage = 'lookup' | 'compute'

/**
 * Reports a failed check. Calls `addError` only, never `startView`: this runs
 * while the product page's view is in flight, and under the
 * `trackViewsManually` the upload workflows configure, `startView` would end
 * it. `stage` says whether the request or the rule failed.
 */
const reportFailure = (exception: unknown, stage: FailureStage) => {
  const extraInfo: Record<string, unknown> = { function: FUNCTION_NAME, stage }
  if (exception instanceof ApolloError) {
    extraInfo.graphQLErrorCodes = exception.graphQLErrors?.map(
      (error) => error.extensions?.code,
    )
    extraInfo.graphQLErrorMessages = exception.graphQLErrors?.map(
      (error) => error.message,
    )
    extraInfo.networkError = exception.networkError?.message
  }
  datadogRum.addError(
    exception,
    generateDataDogContext({
      title: stage === 'lookup' ? LOOKUP_FAILURE_TITLE : COMPUTE_FAILURE_TITLE,
      extraInfo,
    }),
  )
}

const capped = (values: (string | null)[]) =>
  Array.from(new Set(values)).slice(0, ANOMALY_REPORT_LIMIT)

/**
 * One aggregated report per lookup, and only for findings that point at data
 * rather than at stock: a buys SKU the search did not return, a
 * `productPackage` its product does not list, a bundle in a buys group. A
 * genuine shortage is not reported: RUM keeps errors only, and a disabled
 * button is not a usage metric.
 */
const reportAnomalies = (
  anomalies: IAddAllAnomaly[],
  pageSku: string,
  pageProductSubstituted: boolean,
) => {
  if (!anomalies.length) return
  const skusOf = (kind: IAddAllAnomaly['kind']) =>
    capped(anomalies.filter((a) => a.kind === kind).map(({ sku }) => sku))
  datadogRum.addError(
    new Error(ANOMALY_TITLE),
    generateDataDogContext({
      title: ANOMALY_TITLE,
      extraInfo: {
        function: FUNCTION_NAME,
        pageSku,
        pageProductSubstituted,
        promotionId: capped(anomalies.map(({ promotionId }) => promotionId)),
        groupIdentifier: capped(
          anomalies.map(({ groupIdentifier }) => groupIdentifier),
        ),
        missingBuysSkus: skusOf('missingBuysSku'),
        unknownPackageSkus: skusOf('unknownPackage'),
        bundleInBuysSkus: skusOf('bundleInBuys'),
        anomalies: anomalies.slice(0, ANOMALY_REPORT_LIMIT),
        anomalyCount: anomalies.length,
      },
    }),
  )
}

/**
 * The page product as the rule reads a lookup entry, from the product on
 * screen, for when the search left it out. It carries its own `stock`,
 * `outOfStock`, `extraPackages` and `bundle` from `getProductBySKU`. Its
 * promotions are left out: the ones on screen come from the shared fragment,
 * which does not select `productPackage`, and the rule reads packages from
 * whichever returned product carries the promotion.
 */
const pageProductEntry = (product: IProduct): IAvailabilityProduct => ({
  sku: product.sku,
  name: product.name,
  stock: product.stock ?? null,
  outOfStock: product.outOfStock ?? null,
  extraPackages: (product.extraPackages || []).map(({ name, stock }) => ({
    name,
    stock,
  })),
  bundle: product.bundle ? { items: product.bundle.items } : null,
  promotions: [],
})

/** Whether the product reads as having anything to sell. */
const readsInStock = ({ stock, outOfStock }: IAvailabilityProduct) =>
  outOfStock !== true && (!isNumber(stock) || stock > 0)

/**
 * Asks the storefront for the stock of every product the selected product's
 * combo and complex promotions name, and disables the add-all buttons that
 * cannot be fulfilled. Opt-in under
 * `globals.promotions.disableUnfulfillableAddAll`: with the flag off it sends
 * nothing and writes nothing.
 *
 * Mounted once, beside the route table, and keyed on
 * `catalogSlice.selectedProduct` rather than on any template, because the
 * same shortcuts render on containers of any type (the recommended-product
 * page, the meta page, SCS containers that author no `type`). Fires once per
 * product opened, and only when that product has an add-all button on
 * screen, in chunks of `PROMOTION_AVAILABILITY_CHUNK_SIZE` SKUs sent in
 * parallel. Until the answer lands the buttons behave as they always have.
 *
 * An answer for a product the shopper has left is dropped before anything
 * else, unreported.
 *
 * **Fails open**, leaving every button enabled and reporting to Datadog with
 * a `stage` facet, on any exception:
 * - `lookup`: a network or GraphQL error, or a `null` list, on any chunk; or
 *   an empty answer while the product on screen reads in stock, which is
 *   inconsistent (see below).
 * - `compute`: anything the rule itself throws.
 *
 * **Decides from the product on screen** when the search left the page
 * product out. The search drops out-of-stock products on tenants that hide
 * them (and products with no stock record on tenants that manage stock),
 * while the page itself is fetched with that filter off — so a missing page
 * product is the page of an out-of-stock product, and on such a tenant a
 * combo whose every product is out of stock answers `[]`. The page product's
 * entry is then built from the product on screen (`pageProductEntry`), and a
 * page product that reads out of stock disables its buttons rather than
 * failing open.
 *
 * The result is applied from the store as it is when the answer lands, not
 * as it was when the request left, because the cart may have changed
 * meanwhile.
 */
export const usePromotionAddAllAvailability = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const enabled = useAppSelector((state) =>
    isDisableUnfulfillableAddAllOn(state.defaultSlice.config),
  )
  const storefrontName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const sku = useAppSelector((state) => state.catalogSlice.selectedProduct?.sku)
  const shortcuts = useAppSelector(
    (state) => state.catalogSlice.selectedProduct?.promotionsShortcuts,
  )

  // Joined so the effect keys on the SKUs themselves: every cart response
  // rebuilds the shortcuts with a new identity and the same content.
  const skusKey = useMemo(
    () =>
      enabled && sku
        ? collectAddAllSkus(sku, getAddAllPromotions(shortcuts)).join('\n')
        : '',
    [enabled, sku, shortcuts],
  )

  const queryAvailability = usePromotionItemsAvailabilityQuery()

  useEffect(() => {
    if (!skusKey || !sku || !storefrontName || !sessionId) return

    const stillOnScreen = () =>
      store.getState().catalogSlice.selectedProduct?.sku === sku

    const applyAnswer = (answered: IAvailabilityProduct[]) => {
      const {
        catalogSlice: { selectedProduct },
        cartSlice: { items, groups, activePromotions },
        defaultSlice: { config, sessionData },
      } = store.getState()
      if (!selectedProduct || selectedProduct.sku !== sku) return

      let products = answered
      const pageProductSubstituted = !answered.some(
        (product) => product.sku === sku,
      )
      if (pageProductSubstituted) {
        const pageEntry = pageProductEntry(selectedProduct)
        if (!answered.length && readsInStock(pageEntry)) {
          reportFailure(
            new LookupAnswerError(
              'searchProduct answered with no products for an in-stock page product',
            ),
            'lookup',
          )
          return
        }
        products = [...answered, pageEntry]
      }

      try {
        const { availability, anomalies } = computeAddAllAvailability({
          pageProduct: selectedProduct,
          promotions: getAddAllPromotions(selectedProduct.promotionsShortcuts),
          products,
          autoAdd: !!sessionData?.configuration?.promotions?.autoAdd,
          reportMissingBuys:
            sessionData?.configuration?.stock?.hideOutOfStock !== true,
        })
        const { flattedCartItems } = getFlattedCartItems({ items, groups })
        const { promotionsShortcuts } = transformSelectedProduct({
          product: selectedProduct,
          config,
          flattedCartItems,
          activePromotions,
          promotionsAvailability: availability,
        })
        dispatch(
          setPromotionsAvailability({ availability, promotionsShortcuts }),
        )
        reportAnomalies(anomalies, sku, pageProductSubstituted)
      } catch (exception) {
        reportFailure(exception, 'compute')
      }
    }

    Promise.all(
      chunk(skusKey.split('\n')).map((skus) =>
        queryAvailability({ storefrontName, sessionId, skus }).then(
          ({ data }) => {
            // `[]` from one chunk can be legitimate (gift-only SKUs with no
            // price); a `null` list is not an answer at all.
            if (!data?.searchProduct) {
              throw new LookupAnswerError('searchProduct answered null')
            }
            return data.searchProduct
          },
        ),
      ),
    ).then(
      (answers) => applyAnswer(answers.flat()),
      (exception: unknown) => {
        if (stillOnScreen()) reportFailure(exception, 'lookup')
      },
    )
    // `queryAvailability` is stable per client; the SKU list stands in for
    // the shortcuts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skusKey, sku, storefrontName, sessionId])
}

export default usePromotionAddAllAvailability
